import type { Asset, Criticality, PlantState } from "./data";
import { loadRemotePlantState } from "./remoteData";
import { remoteDataEnabled, requireSupabase } from "./supabaseClient";

function dbStatus(status: Asset["status"]) {
  const map: Record<Asset["status"], "in_service" | "out_of_service" | "spare" | "retired"> = {
    "In Service": "in_service",
    "Out of Service": "out_of_service",
    "Spare": "spare",
    "Retired": "retired",
  };
  return map[status];
}

function dbVerification(state: Asset["verificationState"]) {
  const map: Record<Asset["verificationState"], "verified" | "needs_research" | "incomplete"> = {
    "Verified": "verified",
    "Needs Research": "needs_research",
    "Incomplete": "incomplete",
  };
  return map[state];
}

function dbSpecClass(value: Asset["specs"][number]["class"]) {
  if (value === "OEM SPEC") return "oem_spec";
  if (value === "PLANT VALIDATED") return "plant_validated";
  return "as_built_installed";
}

function dbVerificationSource(value: Asset["specs"][number]["verification"]) {
  const map = {
    "OEM manual": "oem_manual",
    "Nameplate": "nameplate",
    "Drawing": "drawing",
    "Measured in field": "measured_in_field",
    "Plant estimate": "plant_estimate",
    "Unknown": "unknown",
  } as const;
  return map[value];
}

async function rpc<T = string>(name: string, args: Record<string, unknown>): Promise<T> {
  const client = requireSupabase();
  const { data, error } = await client.rpc(name, args);
  if (error) throw error;
  return data as T;
}

export async function refreshRemotePlantState(): Promise<PlantState> {
  const state = await loadRemotePlantState();
  if (!state) throw new Error("Remote plant data is unavailable.");
  return state;
}

export async function saveRemoteAssetPassport(asset: Asset, reason = "Technical passport updated from application") {
  return rpc("save_asset_passport", {
    p_asset_id: asset.id,
    p_name: asset.name,
    p_manufacturer: asset.manufacturer,
    p_model: asset.model,
    p_serial_number: asset.serial,
    p_status: dbStatus(asset.status),
    p_criticality: asset.criticality,
    p_verification_state: dbVerification(asset.verificationState),
    p_completeness: asset.completeness,
    p_guide_ti_id: asset.guideTiId ?? "",
    p_notes: asset.notes ?? "",
    p_specs: asset.specs.map((spec, sort_order) => ({
      label: spec.label,
      value: spec.value,
      unit: spec.unit ?? "",
      class: dbSpecClass(spec.class),
      verification: dbVerificationSource(spec.verification),
      note: spec.note ?? "",
      sort_order,
    })),
    p_capabilities: asset.capabilities.map((capability, index) => ({
      key: `capability_${index}_${capability.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
      label: capability.label,
      value: capability.value,
      class: "plant_validated",
      verification: "unknown",
    })),
    p_reason: reason,
  });
}

export type NewAssetInput = {
  tag: string;
  name: string;
  type: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  criticality?: Criticality;
};

export async function addRemoteLineAsset(lineId: string, input: NewAssetInput, reason = "Primary equipment added to line") {
  return rpc<string>("add_line_asset", {
    p_line_id: lineId,
    p_asset_tag: input.tag,
    p_name: input.name,
    p_equipment_type: input.type,
    p_manufacturer: input.manufacturer ?? "",
    p_model: input.model ?? "",
    p_serial_number: input.serial ?? "",
    p_criticality: input.criticality ?? "B",
    p_reason: reason,
  });
}

export async function addRemoteComponent(parentAssetId: string, input: NewAssetInput, reason = "Component added from application") {
  return rpc<string>("add_asset_component", {
    p_parent_asset_id: parentAssetId,
    p_asset_tag: input.tag,
    p_name: input.name,
    p_equipment_type: input.type,
    p_manufacturer: input.manufacturer ?? "",
    p_model: input.model ?? "",
    p_serial_number: input.serial ?? "",
    p_criticality: input.criticality ?? "B",
    p_reason: reason,
  });
}

export async function retireRemoteAsset(assetId: string, reason: string, guideTiWorkOrder = "") {
  return rpc<string>("retire_asset", {
    p_asset_id: assetId,
    p_reason: reason,
    p_guide_ti_work_order: guideTiWorkOrder,
  });
}

export async function moveRemoteAsset(assetId: string, targetLineId: string, targetParentAssetId: string | null, reason: string) {
  return rpc<string>("move_asset", {
    p_asset_id: assetId,
    p_target_line_id: targetLineId,
    p_target_parent_asset_id: targetParentAssetId,
    p_reason: reason,
  });
}

export async function replaceRemoteAsset(
  assetId: string,
  input: { serial?: string; manufacturer?: string; model?: string; reason: string; guideTiWorkOrder?: string; notes?: string },
) {
  return rpc<string>("replace_asset", {
    p_asset_id: assetId,
    p_serial_number: input.serial ?? "",
    p_manufacturer: input.manufacturer ?? "",
    p_model: input.model ?? "",
    p_reason: input.reason,
    p_guide_ti_work_order: input.guideTiWorkOrder ?? "",
    p_notes: input.notes ?? "",
  });
}

export { remoteDataEnabled };
